import {
	Check,
	ChevronDown,
	HelpCircle,
	LogOut,
	Monitor,
	Moon,
	Settings,
	Sun,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useState } from "react";
import { useStore } from "zustand";
import { SettingsModal } from "@/components/app/settings-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePresenceLabel } from "@/hooks/use-presence";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

function getInitials(email?: string) {
	if (!email) {
		return "U";
	}
	return email.charAt(0).toUpperCase();
}

export function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
	const user = useStore(authStore, (state) => state.user);
	const logout = useStore(authStore, (state) => state.logout);
	const profilePictureUrl = useStore(coreStore, (state) =>
		user?.uid ? state.users.get(user.uid)?.profile.profilePhoto : undefined
	);
	const { theme, setTheme } = useTheme();
	const [settingsOpen, setSettingsOpen] = useState(false);
	const statusLabel = usePresenceLabel(user?.uid || "");

	const trigger = collapsed ? (
		<Tooltip>
			<TooltipTrigger asChild>
				<DropdownMenuTrigger asChild>
					<Button className="size-9" size="icon" variant="ghost">
						<Avatar className="size-8 ring-2 ring-border">
							{profilePictureUrl ? (
								<AvatarImage alt="User" src={profilePictureUrl} />
							) : null}
							<AvatarFallback className="text-xs">
								{getInitials(user?.email || "")}
							</AvatarFallback>
						</Avatar>
					</Button>
				</DropdownMenuTrigger>
			</TooltipTrigger>
			<TooltipContent side="right">User Settings</TooltipContent>
		</Tooltip>
	) : (
		<DropdownMenuTrigger asChild>
			<SidebarMenuButton asChild size="lg">
				<Button
					className="w-full justify-start gap-2"
					data-tour="user-menu"
					variant="ghost"
				>
					<Avatar className="size-8">
						{profilePictureUrl ? (
							<AvatarImage alt="User" src={profilePictureUrl} />
						) : null}
						<AvatarFallback className="text-xs">
							{getInitials(user?.email || "")}
						</AvatarFallback>
					</Avatar>
					<div className="flex flex-1 flex-col items-start overflow-hidden">
						<span className="truncate font-semibold text-sm">
							{user?.email || "User"}
						</span>
						<span className="text-muted-foreground text-xs">{statusLabel}</span>
					</div>
					<ChevronDown className="size-3.5" />
				</Button>
			</SidebarMenuButton>
		</DropdownMenuTrigger>
	);

	const menuContent = (
		<DropdownMenuContent
			align="end"
			className="w-56"
			side={collapsed ? "right" : undefined}
		>
			<DropdownMenuLabel>
				<div className="flex flex-col space-y-1">
					<p className="font-medium text-sm">{user?.email || "User"}</p>
					<p className="text-muted-foreground text-xs">{statusLabel}</p>
				</div>
			</DropdownMenuLabel>
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={() => setSettingsOpen(true)}>
				<Settings className="size-3.5" />
				Settings
			</DropdownMenuItem>
			<DropdownMenuSub>
				<DropdownMenuSubTrigger className="gap-2">
					<HelpCircle className="size-3.5" />
					<span>Start Tour</span>
				</DropdownMenuSubTrigger>
				<DropdownMenuPortal>
					<DropdownMenuSubContent>
						<DropdownMenuItem
							onClick={async () => {
								const startTour = uiStore.getState().startTour;
								await startTour();
							}}
						>
							All Tours
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={async () => {
								const resetTour = coreStore.getState().resetTour;
								await resetTour("app");
							}}
						>
							App Tour
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={async () => {
								const resetTour = coreStore.getState().resetTour;
								await resetTour("message");
							}}
						>
							Message Tour
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={async () => {
								const resetTour = coreStore.getState().resetTour;
								await resetTour("voice");
							}}
						>
							Video Tour
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={async () => {
								const resetTour = coreStore.getState().resetTour;
								await resetTour("broadcast");
							}}
						>
							Broadcast Tour
						</DropdownMenuItem>
					</DropdownMenuSubContent>
				</DropdownMenuPortal>
			</DropdownMenuSub>
			<DropdownMenuSub>
				<DropdownMenuSubTrigger className="gap-2">
					{theme === "dark" && <Moon className="size-3.5" />}
					{theme === "light" && <Sun className="size-3.5" />}
					{theme === "system" && <Monitor className="size-3.5" />}
					<span>Theme</span>
				</DropdownMenuSubTrigger>
				<DropdownMenuPortal>
					<DropdownMenuSubContent>
						<DropdownMenuItem
							className={theme === "light" ? "bg-accent" : ""}
							onClick={() => setTheme("light")}
						>
							<Sun className="size-3.5" />
							Light
							{theme === "light" && <Check className="ml-auto size-3.5" />}
						</DropdownMenuItem>
						<DropdownMenuItem
							className={theme === "dark" ? "bg-accent" : ""}
							onClick={() => setTheme("dark")}
						>
							<Moon className="size-3.5" />
							Dark
							{theme === "dark" && <Check className="ml-auto size-3.5" />}
						</DropdownMenuItem>
						<DropdownMenuItem
							className={theme === "system" ? "bg-accent" : ""}
							onClick={() => setTheme("system")}
						>
							<Monitor className="size-3.5" />
							System
							{theme === "system" && <Check className="ml-auto size-3.5" />}
						</DropdownMenuItem>
					</DropdownMenuSubContent>
				</DropdownMenuPortal>
			</DropdownMenuSub>
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={logout} variant="destructive">
				<LogOut className="size-3.5" />
				Log out
			</DropdownMenuItem>
		</DropdownMenuContent>
	);

	const dropdown = (
		<DropdownMenu>
			{trigger}
			{menuContent}
		</DropdownMenu>
	);

	return (
		<>
			<SettingsModal onOpenChange={setSettingsOpen} open={settingsOpen} />
			{collapsed ? (
				dropdown
			) : (
				<SidebarMenu>
					<SidebarMenuItem>{dropdown}</SidebarMenuItem>
				</SidebarMenu>
			)}
		</>
	);
}
